import { unwrapResult } from '@reduxjs/toolkit';
import {
  ClaimDTOStatusEnum,
  ClaimProposalDTOStatusEnum,
  ClaimResponseDocumentDTO,
  CreateClaimItemProposalDTO,
  OrderDTOStatusIdEnum,
  PaymentPlanDTOStatusEnum,
  PaymentPlanInstalmentDTOStatusEnum,
  OrderCustomerActionDTOTypeIdEnum,
  EvidenceChaserDTOTypeEnum,
  EvidenceChaserDTOStatusEnum,
  ClaimActivityType,
} from '@reposit/api-client';
import { push } from 'connected-react-router';
import { Location } from 'history';
import { get } from 'lodash';
import moment from 'moment';
import React, { Fragment, useEffect, useReducer, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import { useAppDispatch } from '../..';
import HelpIcon from '../../assets/svg/help.svg';
import WaitingGraphic from '../../assets/svg/waiitng-illustration.svg';
import { RepositTheme } from '../../base/theme';
import ApproveClaimResponseUpdateModal from '../../components/ApproveClaimResponseUpdateModal/ApproveClaimResponseUpdateModal';
import { RoundButton } from '../../components/Button';
import ClaimAction from '../../components/ClaimAction';
import ClaimAppeal from '../../components/ClaimAppeal/index';
import ClaimDocuments from '../../components/ClaimDocuments';
import ClaimItems from '../../components/ClaimItems';
import FlashMessage from '../../components/FlashMessage/index';
import { FullPageLoader } from '../../components/Loading/index';
import RepositCard from '../../components/Reposit/RepositCard';
import { Caption, Header2, P2 } from '../../components/Typography';
import { getCurrentCustomerId, getCurrentUserId } from '../../redux/account/account.selectors';
import { getActiveClaimResponseConfirmModal } from '../../redux/claim/claim.selectors';
import {
  fetchClaimRequested,
  RESPOND_TO_CLAIM_STORE_KEY,
  setIsClaimResponseUpdateModalShowing,
  updateClaimResponseRequested,
  UPDATE_CLAIM_RESPONSE_STORE_KEY,
} from '../../redux/claim/claim.actions';
import { getClaimById, getIsClaimResponseUpdateModalShowing, getOrderedClaimDocuments } from '../../redux/claim/claim.selectors';
import {
  createClaimActivityThunk,
  createClaimItemProposalThunk,
  updateClaimItemProposalThunk,
} from '../../redux/claim/claim.thunk';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import { PAY_STORE_KEY } from '../../redux/order-customer-actions/order-customer-actions.actions';
import { fetchOrderCustomerRequested } from '../../redux/order/order.actions';
import { getCurrentOrderCustomer } from '../../redux/order/order.selectors';
import { getPaymentPlanByClaimId } from '../../redux/payment-plan/payment-plan.selectors';
import { AppState } from '../../redux/root.reducer';
import { getAddressByClaimId } from '../../redux/selectors/address.selectors';
import {
  getArbitrationAdminFeeOrder,
  getClaimOrder,
  getFirstAgentProposalTotalAmount,
  getFirstTenantProposal,
  getHasTenantAcceptedAnyCharges,
  getHasTenantAutoAcceptedAnyCharges,
  getHasTenantDisputed,
  getHasTenantSubmitted,
  getIsTenantAuthorisingPayment,
  getIsTenantDeciding,
  getIsTenantNegotiating,
  getLatestAgentProposal,
  getLatestAgentProposalResponseDeadline,
  getLatestProposalTotalAmount,
  getMediationClaimItemsByClaimId,
  getSecondAgentProposal,
  getSecondAgentProposalTotalAmount,
} from '../../redux/selectors/mediation.selectors';
import { getAddressFirstLine, penceToPounds } from '../../utils/common.utils';
import { FLASH_MESSAGE_TIMOUT, useFlashMessage } from '../FlashMessage/index';
import MediationIntroduction from '../MediationIntroduction';
import { getOrderCustomerActionsByIds } from '../../redux/order-customer-actions/order-customer-actions.selectors';
import ClaimHeader from './ClaimHeader';

interface ClaimProps {
  location: Location<any>;
  match: any;
}

enum ClaimProposalResponseType {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

const ArbitratingTitle = styled(Header2)`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ArbitratingCaption = styled(Caption)`
  text-align: center;
  margin-bottom: 48px;
`;

const FlashWrapper = styled.div`
  margin: 24px 0;
`;

const ClaimFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 16px 0 12px;
`;

const ClaimTotal = styled(P2) <{ warning: boolean }>`
  text-align: right;
  margin: 0;

  span {
    color: ${(props) => (props.warning ? RepositTheme.colors.negative : RepositTheme.colors.body)};
    font-family: ${(props) => props.theme.typography.face.primary};
    font-weight: bold;
    font-size: 20px;
    margin: 0 16px 0 10px;
  }
`;

export const LiabilityTotal = styled(P2)`
  text-align: right;
  margin: 0;
  color: ${RepositTheme.colors.negative};

  span {
    font-family: ${(props) => props.theme.typography.face.primary};
    font-weight: bold;
    font-size: 20px;
    margin: 0 16px 0 10px;
  }
`;

const ClaimTotalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  padding: 16px 16px 28px;
`;

const AwaitingArbitrationHeader: React.FC<{ orgName: string }> = ({ orgName }) => {
  const phrase = orgName ? orgName : 'letting agent/landlord';
  return (
    <Fragment>
      <Row>
        <Col lg={12}>
          <ArbitratingTitle>Awaiting response from {phrase}</ArbitratingTitle>
          <ArbitratingCaption>
            {`There's nothing else you need to do for now. We'll notify you via email when we have an update for you.`}
          </ArbitratingCaption>
        </Col>
      </Row>
      <Row>
        <Col lg={12} style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src={WaitingGraphic} style={{ maxWidth: '643px', width: '100%' }} alt="Waiting People" />
        </Col>
      </Row>
    </Fragment>
  );
};

const WithAdjudicatorHeader: React.FC = () => (
  <Fragment>
    <Row>
      <Col lg={12}>
        <ArbitratingTitle>Your case is with the independent adjudicator</ArbitratingTitle>
        <ArbitratingCaption>
          {`We expect your independent adjudication report to be available within the next couple of weeks. The adjudicator will review all evidence submitted by both parties and provide an impartial, final decision. You'll be notified immediately once the decision is available.`}
        </ArbitratingCaption>
      </Col>
    </Row>
    <Row>
      <Col lg={12} style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <img src={WaitingGraphic} style={{ maxWidth: '643px', width: '100%' }} alt="Waiting People" />
      </Col>
    </Row>
  </Fragment>
);

const HelpButton = styled(RoundButton)`
  margin-right: 10px;
`;

const MaxLiabilityWrapper = styled.div`
  display: flex;
  align-items: center;
`;

type Action = { type: 'setHasGoneBack'; payload: boolean } | { type: 'decrementCount' };
type State = { hasGoneBack: boolean; hasGoneBackCount: number };

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'setHasGoneBack':
      return {
        ...state,
        hasGoneBack: action.payload,
        hasGoneBackCount: action.payload ? state.hasGoneBackCount + 1 : 0,
      };
    case 'decrementCount':
      return {
        ...state,
        hasGoneBackCount: state.hasGoneBackCount - 1,
      };
    default:
      return state;
  }
}

const Claim: React.FC<ClaimProps> = ({ match, location }) => {
  const dispatch = useAppDispatch();

  const [{ hasGoneBack, hasGoneBackCount }, reducerDispatch] = useReducer(reducer, { hasGoneBack: false, hasGoneBackCount: 0 });

  const setHasGoneBack = (payload: boolean, force?: boolean) => {
    if (!payload && hasGoneBackCount === 2 && !force) {
      reducerDispatch({ type: 'decrementCount' });
    } else {
      reducerDispatch({ type: 'setHasGoneBack', payload });
    }
  };

  const { claimId } = match.params;
  const claim = useSelector((state: AppState) => getClaimById(state, claimId));

  const claimOrder = useSelector(getClaimOrder(claimId));
  const currentOrderCustomer = useSelector(getCurrentOrderCustomer);

  const isModalShowing = useSelector(getIsClaimResponseUpdateModalShowing);
  const activeModal = useSelector(getActiveClaimResponseConfirmModal);
  const currentCustomerId = useSelector(getCurrentCustomerId);
  const isTenantAuthorisingPayment = useSelector((state: AppState) => getIsTenantAuthorisingPayment(state, claimId));

  const currentUserId = useSelector(getCurrentUserId);
  const claimRespondents = get(claim, 'respondents', []);
  const claimRespondent = claimRespondents.find((claimRespondent: any) => claimRespondent.userId === currentUserId);
  const claimRespondentId = get(claimRespondent, 'id');
  const claimResponseId = get(claimRespondent, 'claimResponse.id');
  const claimDisputeMessage = get(claim, 'tenantDisputeMessage');
  const claimDetails = get(claim, 'details', '');
  const isMediationEnabled = get(claim, 'mediationEnabled', false);
  const organizationName = get(claim, 'organization.name', '');

  const { pathname, search } = location;
  const [urlFlagAdded, setUrlFlagAdded] = useState(false);

  useEffect(() => {
    if (isMediationEnabled && !search && !urlFlagAdded) {
      dispatch(push(`${pathname}?mediationEnabled=true`));
      setUrlFlagAdded(true);
    }
  }, [isMediationEnabled, dispatch, search, pathname, urlFlagAdded, setUrlFlagAdded]);

  const address = useSelector((state: AppState) => getAddressByClaimId(state, claimId));

  const claimResponseDocuments =
    claim && claim.responseDocuments ? claim.responseDocuments.map((rd: ClaimResponseDocumentDTO) => rd.document) : [];
  const [flashMessage, dismissFlashMessage] = useFlashMessage([RESPOND_TO_CLAIM_STORE_KEY, PAY_STORE_KEY]);

  const orderedClaimDocuments = useSelector((state: AppState) => getOrderedClaimDocuments(state, claimId));

  const isLoadingSelector = createLoadingSelector([UPDATE_CLAIM_RESPONSE_STORE_KEY]);
  const isLoading = useSelector(isLoadingSelector);

  const secondAgentProposal = useSelector((state: AppState) => getSecondAgentProposal(state, claimId));

  const isAgentSecondProposalDraft = secondAgentProposal && secondAgentProposal.status === ClaimProposalDTOStatusEnum.DRAFT;

  const latestAgentProposal = useSelector((state: AppState) => getLatestAgentProposal(state, claimId));
  const isLatestAgentProposalFinal = latestAgentProposal && ((latestAgentProposal as any).type as any) === 'FINAL';
  const latestAgentProposalResponseDeadline = useSelector((state: AppState) =>
    getLatestAgentProposalResponseDeadline(state, claimId)
  );
  const today = moment().startOf('day');
  const daysRemaining = moment(latestAgentProposalResponseDeadline, '').diff(today, 'day');

  const tenantProposal = useSelector((state: AppState) => getFirstTenantProposal(state, claimId));
  const tenantProposalId = get(tenantProposal, 'id', '');
  const isTenantNegotiating = useSelector((state: AppState) => getIsTenantNegotiating(state, claimId));
  const hasTenantAutoAcceptedAnyCharges = useSelector((state: AppState) => getHasTenantAutoAcceptedAnyCharges(state, claimId));
  const hasTenantAcceptedAnyCharges = useSelector((state: AppState) => getHasTenantAcceptedAnyCharges(state, claimId));
  const hasTenantDisputed = useSelector((state: AppState) => getHasTenantDisputed(state, claimId));
  const hasTenantSubmitted = useSelector((state: AppState) => getHasTenantSubmitted(state, claimId));
  const isTenantDeciding = useSelector((state: AppState) => getIsTenantDeciding(state, claimId));
  const claimItems = useSelector((state: AppState) => getMediationClaimItemsByClaimId(state, claimId));
  const arbitrationFeeOrder = useSelector((state: AppState) => getArbitrationAdminFeeOrder(state, claimId));
  const arbitrationFeeOrderPaid = arbitrationFeeOrder && arbitrationFeeOrder.statusId === OrderDTOStatusIdEnum.COMPLETE;

  const latestTotalProposalAmount = useSelector((state: AppState) => getLatestProposalTotalAmount(state, claimId));
  const firstAgentProposalAmount = useSelector((state: AppState) => getFirstAgentProposalTotalAmount(state, claimId));
  const secondAgentProposalAmount = useSelector((state: AppState) => getSecondAgentProposalTotalAmount(state, claimId));

  const totalProposalAmount = hasGoneBack ? secondAgentProposalAmount || firstAgentProposalAmount : latestTotalProposalAmount;

  const isAwaitingEvidenceReview = !!(claim && claim.status === ClaimDTOStatusEnum.AWAITINGEVIDENCEREVIEW);
  const isInEvidenceReview = !!(claim && claim.status === ClaimDTOStatusEnum.EVIDENCEREVIEW);
  const isInMediation = !!(claim && claim.status === ClaimDTOStatusEnum.MEDIATION);
  const isArbitrated = !!(claim && claim.status === ClaimDTOStatusEnum.ARBITRATED);

  const hasDisputedProposal = !!(claim && claim.proposals.some((p: any) => p.status === ClaimProposalDTOStatusEnum.DISPUTED));
  const evidenceChasers = get(claim, 'evidenceChasers', []);
  const hasEvidenceChasers = !!evidenceChasers.length;
  // hasn't created a chaser yet but is waiting to
  const preADRWithoutChaser = isAwaitingEvidenceReview && hasDisputedProposal && !hasEvidenceChasers;
  // has started to create a chaser
  const hasPendingPreADRChaser = evidenceChasers.some(
    (chaser: any) => chaser.type === EvidenceChaserDTOTypeEnum.PREADR && chaser.status === EvidenceChaserDTOStatusEnum.PENDING
  );
  const isClaimAwaitingArbitration = !!(claim && claim.status === ClaimDTOStatusEnum.AWAITINGARBITRATION);
  // all of these mean that the tenant should see the awaiting adr page
  const isAwaitingArbitration = preADRWithoutChaser || hasPendingPreADRChaser || isClaimAwaitingArbitration;
  // the claim stays AWAITING_ARBITRATION once sent, so the send date is the only signal
  const adjudicationSentAt = get(claim, 'adjudicationSentAt');
  const isWithAdjudicator =
    isClaimAwaitingArbitration && !!adjudicationSentAt && !moment().isBefore(moment(adjudicationSentAt).startOf('day'));

  const isAwaitingSubstantiation = !!(claim && claim.status === ClaimDTOStatusEnum.AWAITINGSUBSTANTIATION);
  const isAwaitingRespondents = !!(claim && claim.status === ClaimDTOStatusEnum.AWAITINGRESPONDENTS);
  const isAwaitingArbAdminFee = !!(claim && claim.status === ClaimDTOStatusEnum.AWAITINGARBITRATIONADMINFEE);

  const needsResponseOrPayment = !!(
    claim &&
    !isAwaitingArbitration &&
    (isAwaitingRespondents || isAwaitingArbAdminFee || isAwaitingEvidenceReview || isInEvidenceReview)
  );

  const claimItemsViewOnly = !isTenantNegotiating || hasGoneBack;
  const claimLimit: number = get(claim, 'limit', 0);
  const claimLimitExceeded = totalProposalAmount && totalProposalAmount > claimLimit;
  const showClaimAppeal = (isAwaitingArbitration || isAwaitingSubstantiation) && hasTenantDisputed && arbitrationFeeOrderPaid;
  const hasTenantResponded =
    hasTenantAcceptedAnyCharges || isTenantNegotiating || isTenantAuthorisingPayment || hasTenantDisputed;
  const showMediationIntroductionInitialState = !!(
    !hasTenantResponded &&
    isMediationEnabled &&
    !hasTenantSubmitted &&
    !secondAgentProposal
  );

  const [showMediationIntroduction, setShowMediationIntroduction] = useState<boolean>(showMediationIntroductionInitialState);

  const nextActionIds = get(currentOrderCustomer, 'nextAction.orderCustomerActionIds', []);

  const nextActions = useSelector(getOrderCustomerActionsByIds(nextActionIds));
  const remainingBalanceAction = nextActions.find((a) => a.typeId === OrderCustomerActionDTOTypeIdEnum.CLAIMPAYREMAININGBALANCE);

  const amountRemaining = get(remainingBalanceAction, 'details.amountRemaining');
  const currentOrderCustomerFee = get(currentOrderCustomer, 'fee');

  const nothingToPay = currentOrderCustomerFee === 0 || amountRemaining === 0;

  useEffect(() => {
    setShowMediationIntroduction(showMediationIntroductionInitialState);
  }, [showMediationIntroductionInitialState]);

  const paymentPlan = useSelector(getPaymentPlanByClaimId(claimId, currentCustomerId));
  const hasPaymentPlan =
    paymentPlan &&
    (paymentPlan.status === PaymentPlanDTOStatusEnum.ACTIVE || paymentPlan.status === PaymentPlanDTOStatusEnum.PENDING);

  const createCounterProposal = async (payload: CreateClaimItemProposalDTO) => {
    return dispatch(createClaimItemProposalThunk({ claimId, claimProposalId: tenantProposalId, payload })).then(unwrapResult);
  };

  const updateCounterProposal = async (payload: CreateClaimItemProposalDTO, claimItemProposalId: string) => {
    return dispatch(
      updateClaimItemProposalThunk({ claimId, claimProposalId: tenantProposalId, claimItemProposalId, payload })
    ).then(unwrapResult);
  };

  const tooltipText = `With Reposit, the landlord is covered for up to 8 weeks worth of rent. This means that you are only liable for up to £${penceToPounds(
    claimLimit
  )}, even if the total claim exceeds this amount.`;

  useEffect(() => {
    dispatch(fetchClaimRequested(claimId));
  }, [dispatch, claimId]);

  useEffect(() => {
    if (currentCustomerId && claimOrder && !currentOrderCustomer) {
      dispatch(
        fetchOrderCustomerRequested({
          customerId: currentCustomerId,
          orderId: claimOrder.id,
          source: {
            sourceType: 'route-load',
            sourceName: 'claim-page',
            route: window.location.pathname,
            reason: 'claim-order-bootstrap',
            claimId,
          },
        })
      );
    }
  }, [claimId, claimOrder, currentCustomerId, currentOrderCustomer, dispatch]);

  const [previousEvent, setPreviousEvent] = useState<ClaimActivityType | null>(null);

  let eventToSend: ClaimActivityType | null = null;
  // This closely maps to the rendering logic in the ClaimAction component. 
  if (showMediationIntroduction) {
    eventToSend = ClaimActivityType.WELCOMEPAGE;
  } else if (activeModal === ClaimProposalResponseType.ACCEPTED) {
    eventToSend = ClaimActivityType.ACCEPTCHARGESMODAL;
  } else if (hasTenantAcceptedAnyCharges && !hasGoneBack) {
    eventToSend = ClaimActivityType.ACCEPTCHARGESPAYMENTPAGE;
  } else if (hasTenantDisputed && (!hasGoneBack || (hasGoneBack && claimDisputeMessage && hasGoneBackCount < 2))) {
    // We don't currently have an event for this page
    eventToSend = null;
  } else if (previousEvent !== ClaimActivityType.ACCEPTCHARGESMODAL && isTenantNegotiating && !hasGoneBack) {
    eventToSend = ClaimActivityType.REVIEWCHARGES;
  } else if (isTenantAuthorisingPayment) {
    eventToSend = ClaimActivityType.PROPOSALPAYMENTPAGE;
  } else if (previousEvent !== ClaimActivityType.ACCEPTCHARGESMODAL) { // If we don't suppress this we will send redundant events
    eventToSend = ClaimActivityType.INITIALCHARGES;
  }

  // If these are out of sync we don't want to pre-emptively fire any events
  const mediationIntroductionMatches = showMediationIntroductionInitialState === showMediationIntroduction;

  useEffect(() => {
    const isInitialRound = claim && (claim.proposals.length === 1 || (claim.proposals || []).find((p: any) => p.round === 2)?.status === ClaimProposalDTOStatusEnum.DRAFT);

    if (isInitialRound && mediationIntroductionMatches && eventToSend && previousEvent !== eventToSend) {
      setPreviousEvent(eventToSend);
      dispatch(createClaimActivityThunk({ claimId, activityType: eventToSend }));
    }
  }, [claim, claimId, dispatch, eventToSend, previousEvent, mediationIntroductionMatches]);

  if (!claim || !claim.items || !claim.documents) return <FullPageLoader />;

  /**
   * `true` when at least one instalment on the payment plan has a `FAILED`
   * status. A failed instalment means the tenant's payment couldn't be
   * collected and they must revisit the payment plan page to resolve it.
   */
  const hasPPFailedInstalment = !!(
    paymentPlan?.instalments?.some((i) => i.status === PaymentPlanInstalmentDTOStatusEnum.FAILED)
  );

  /**
   * `true` when the payment plan exists but is still in the `PENDING` state,
   * meaning the tenant has not yet completed the payment plan setup flow.
   */
  const isPPPendingSetup = paymentPlan?.status === PaymentPlanDTOStatusEnum.PENDING;

  /**
   * Redirects to the payment plan only when immediate action is required: a
   * failed instalment that must be resolved, or a plan that is still pending
   * setup. An `ACTIVE` payment plan (where the tenant is already paying off
   * the charges in instalments) should not block the tenant from viewing the
   * claim details.
   */
  if (hasPaymentPlan && paymentPlan && (hasPPFailedInstalment || isPPPendingSetup)) {
    return <Redirect to={`/payment-plan/${paymentPlan.id}`} />;
  }

  if (isArbitrated) {
    return <Redirect to={`/arbitrations/${claim.arbitration.id}`} />;
  }

  if (!isAwaitingArbitration && !needsResponseOrPayment && !isArbitrated && !isInMediation && !isAwaitingSubstantiation) {
    return <Redirect to="/" />;
  }

  if (showMediationIntroduction) {
    return (
      <MediationIntroduction setShowMediationIntroduction={setShowMediationIntroduction} organizationName={organizationName} />
    );
  }

  const showChargesHaveBeenRaised =
    isAwaitingSubstantiation || isTenantDeciding || isAwaitingArbAdminFee || isAwaitingArbitration || hasTenantAcceptedAnyCharges;

  const isAtRootOfClaimDisputeForSecondProposal =
    (claimDisputeMessage && hasGoneBackCount === 2) || (!claimDisputeMessage && hasGoneBack);

  return (
    <Fragment>
      {isModalShowing && (
        <ApproveClaimResponseUpdateModal
          onAccept={() =>
            // investigate this
            dispatch(updateClaimResponseRequested({ message: '', claimId, claimRespondentId, claimResponseId }))
          }
          onDismiss={() => dispatch(setIsClaimResponseUpdateModalShowing(false))}
          isLoading={isLoading}
        />
      )}
      {flashMessage ? (
        <Container>
          <Row>
            <Col sm={12}>
              <FlashWrapper>
                <FlashMessage onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMOUT} payload={flashMessage} />
              </FlashWrapper>
            </Col>
          </Row>
        </Container>
      ) : null}

      <Container style={{ paddingTop: 0 }}>
        {isAwaitingArbitration ||
          hasTenantSubmitted ||
          isAgentSecondProposalDraft ||
          (isAwaitingSubstantiation && hasTenantDisputed) ? (
            isWithAdjudicator ? (
              <WithAdjudicatorHeader />
            ) : (
              <AwaitingArbitrationHeader orgName={organizationName} />
            )
          ) : (
            <ClaimHeader
              hasTenantAutoAcceptedAnyCharges={hasTenantAutoAcceptedAnyCharges}
              hasTenantAcceptedAnyCharges={hasTenantAcceptedAnyCharges}
              isTenantDeciding={isTenantDeciding}
              isLatestAgentProposalFinal={!!isLatestAgentProposalFinal}
              address={address && getAddressFirstLine(address)}
              daysRemaining={daysRemaining}
              organizationName={organizationName}
              nothingToPay={nothingToPay}
            />
          )}
        {(needsResponseOrPayment ||
          (isAwaitingSubstantiation && !hasTenantDisputed) ||
          (claim && !claim.mediationEnabled && !isAwaitingArbitration) ||
          (isInMediation && !hasTenantSubmitted && !isAgentSecondProposalDraft)) &&
          !nothingToPay && (
            <ClaimAction
              claim={claim}
              claimResponseDocuments={claimResponseDocuments}
              createCounterProposal={createCounterProposal}
              updateCounterProposal={updateCounterProposal}
              claimItems={claimItems}
              setHasGoneBack={(val, force) => setHasGoneBack(val, force)}
              hasGoneBack={hasGoneBack}
              hasGoneBackCount={hasGoneBackCount}
              organizationName={organizationName}
              setShowMediationIntroduction={setShowMediationIntroduction}
            />
          )}

        {(!isTenantNegotiating || hasGoneBack) &&
          // check if they're authorising payment
          !isTenantAuthorisingPayment &&
          // check if they have disputed - but allow them if they are at the root of the choices having gone back
          (!isAwaitingArbAdminFee || isAtRootOfClaimDisputeForSecondProposal) &&
          // accept go back only has one layer
          (!hasTenantAcceptedAnyCharges || (hasGoneBack && hasTenantAcceptedAnyCharges)) && (
            <Row>
              <Col lg={12}>
                <RepositCard flush title={showChargesHaveBeenRaised ? 'Charges Raised' : 'Your Proposal'}>
                  <ClaimItems
                    viewOnly={claimItemsViewOnly}
                    createCounterProposal={createCounterProposal}
                    updateCounterProposal={updateCounterProposal}
                    isTenantNegotiating={isTenantNegotiating}
                    claimItems={claimItems}
                    hasGoneBack={hasGoneBack}
                  />
                  <ClaimFooter>
                    <ClaimTotalWrapper>
                      <ClaimTotal warning={false}>
                        Total: <span>£{penceToPounds(totalProposalAmount || 0)}</span>
                      </ClaimTotal>
                      {claimLimitExceeded ? (
                        <MaxLiabilityWrapper>
                          <HelpButton icon={HelpIcon} data-tip={tooltipText} />
                          <LiabilityTotal>
                            Your maximum liability: <span>£{penceToPounds(claimLimit)}</span>
                          </LiabilityTotal>
                        </MaxLiabilityWrapper>
                      ) : null}
                    </ClaimTotalWrapper>
                  </ClaimFooter>
                </RepositCard>
              </Col>
            </Row>
          )}
        {showClaimAppeal ? <ClaimAppeal claimId={claim.id} /> : null}
        {claimDetails &&
          // same logic here as above about going back
          ((hasGoneBack && hasTenantAcceptedAnyCharges) ||
            isAtRootOfClaimDisputeForSecondProposal ||
            (!isTenantAuthorisingPayment && !isAwaitingArbAdminFee && !hasTenantAcceptedAnyCharges)) ? (
            <Row>
              <Col>
                <RepositCard title="Supporting Information">
                  <P2>{claimDetails}</P2>
                </RepositCard>
              </Col>
            </Row>
          ) : null}
        {orderedClaimDocuments &&
          orderedClaimDocuments.length &&
          // same logic here as above about going back
          ((hasGoneBack && hasTenantAcceptedAnyCharges) ||
            isAtRootOfClaimDisputeForSecondProposal ||
            (!isTenantAuthorisingPayment && !isAwaitingArbAdminFee && !hasTenantAcceptedAnyCharges)) ? (
            <Row>
              <Col lg={12}>
                <ClaimDocuments documents={orderedClaimDocuments} />
              </Col>
            </Row>
          ) : null}
      </Container>
    </Fragment>
  );
};

export default Claim;
